import { useEffect, useRef, type RefObject } from "react";

// Large enough to survive Popover.tsx's own 8px anchor gap during a
// straight mouse move from the trigger into the popover, which an instant
// check would misread as "the user left".
const CLOSE_DELAY_MS = 400;

/**
 * Closes an already-open toolbar dropdown a short delay after the pointer
 * leaves both the trigger and its popover panel; opening is click-only and
 * this hook never opens anything.
 *
 * `refs` is the same `[wrapperRef, popoverRef]` pair `useClickOutside`
 * tracks, needed here too because Popover.tsx portals its content outside
 * the trigger's own DOM subtree.
 *
 * Attachment is deferred by one animation frame because `popoverRef.current`
 * is still `null` at the instant `open` flips true — Popover.tsx only
 * mounts the portaled `<div>` one render later.
 */
export function useCloseOnLeave(
  refs: RefObject<HTMLElement | null>[],
  open: boolean,
  onClose: () => void
) {
  const closeTimer = useRef<number | undefined>(undefined);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let attached: HTMLElement[] = [];

    function handleEnter() {
      window.clearTimeout(closeTimer.current);
    }
    function handleLeave() {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = window.setTimeout(() => onCloseRef.current(), CLOSE_DELAY_MS);
    }

    const raf = requestAnimationFrame(() => {
      if (cancelled) return;
      attached = refs.map((r) => r.current).filter((el): el is HTMLElement => el !== null);
      for (const el of attached) {
        el.addEventListener("mouseenter", handleEnter);
        el.addEventListener("mouseleave", handleLeave);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      window.clearTimeout(closeTimer.current);
      for (const el of attached) {
        el.removeEventListener("mouseenter", handleEnter);
        el.removeEventListener("mouseleave", handleLeave);
      }
    };
    // `refs` omitted: the array literal is a fresh identity every render,
    // but the `useRef` objects inside it are not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
